import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSupplierUpload,
  fetchSupplierUpload,
  fetchSupplierUploads,
} from './supplierUploadApi';
import { SUPPLIER_DRAFTS_QUERY_KEY } from './useSupplierDrafts';

export const SUPPLIER_UPLOADS_QUERY_KEY = ['supplierUploads'];
export const supplierUploadQueryKey = (uploadId) => [...SUPPLIER_UPLOADS_QUERY_KEY, uploadId];

// AMENDMENT (S7.4): POST /supplier/uploads is SYNCHRONOUS — there is no
// quarantine/parse polling flow. The list/detail queries below are plain
// reads (deep-link refresh, the drafts inbox's "grouped by source upload"
// header); the create mutation resolves once ingestion has fully run.

export const useSupplierUploads = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: SUPPLIER_UPLOADS_QUERY_KEY,
    queryFn: fetchSupplierUploads,
    staleTime: 0,
    enabled,
  });

export const useSupplierUpload = (uploadId, { enabled = true } = {}) =>
  useQuery({
    queryKey: supplierUploadQueryKey(uploadId),
    queryFn: () => fetchSupplierUpload(uploadId),
    enabled: enabled && !!uploadId,
    staleTime: 0,
  });

// The create mutation. Errors (400/422/429) carry the rejection envelope via
// SupplierUploadApiError — the dropzone reads `error.payload.error` for the
// code-mapped human copy rather than a generic toast (phase-4 mutation UX:
// inline recoverable state, not a disappearing notification).
export const useCreateSupplierUpload = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSupplierUpload,
    onSuccess: async () => {
      // A new upload adds an item to the uploads list (grouping header on the
      // drafts inbox) and always produces at least one draft on success.
      await queryClient.invalidateQueries({ queryKey: SUPPLIER_UPLOADS_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: SUPPLIER_DRAFTS_QUERY_KEY });
    },
  });
};
